import React, { useState, useEffect, useRef } from "react";
import { Instagram, ExternalLink, Play, Pause, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useResponsive } from "../hooks/useResponsive";
import { useScrollAnimation } from "../hooks/useAnimations";
import InteractiveCard from "./InteractiveCard";
import { colors } from "../styles/colors";

const InstagramFeed = () => {
    const { t } = useTranslation();
    const { isMobile } = useResponsive();
    const [sectionRef, isVisible] = useScrollAnimation(0.2);

    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const videoRef = useRef(null);

    useEffect(() => {
        const fetchInstagramPost = async () => {
            try {
                setLoading(true);
                setError(null);

                // In development, use relative path; in production Netlify handles the function
                const functionUrl = process.env.NODE_ENV === "development"
                    ? "/.netlify/functions/instagram"
                    : "/.netlify/functions/instagram";

                const response = await fetch(functionUrl);

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to fetch Instagram post");
                }

                const data = await response.json();
                setPost(data);
            } catch (err) {
                console.error("Instagram fetch error:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchInstagramPost();
    }, []);

    // Format timestamp to readable date
    const formatDate = (timestamp) => {
        if (!timestamp) return "";
        const date = new Date(timestamp);
        return date.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    // Truncate caption for display
    const truncateCaption = (text, maxLength = 150) => {
        if (!text) return "";
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength).trim() + "...";
    };

    // Don't render section if there's an error (graceful degradation)
    if (error && !post) {
        // Only show error in development
        if (process.env.NODE_ENV === "development") {
            return (
                <section className="py-12" style={{ background: colors.gradients.heroSolid }}>
                    <div className="max-w-4xl mx-auto px-4">
                        <div className="flex items-center justify-center gap-3 p-6 rounded-xl"
                            style={{ backgroundColor: colors.glass.dark, border: `1px solid ${colors.border}` }}>
                            <AlertCircle size={24} style={{ color: colors.accent.hover }} />
                            <p style={{ color: colors.text.secondary }}>
                                Instagram integration not configured. Set INSTAGRAM_ACCESS_TOKEN in Netlify.
                            </p>
                        </div>
                    </div>
                </section>
            );
        }
        return null;
    }

    return (
        <section
            ref={sectionRef}
            className={`py-16 sm:py-20 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                }`}
            style={{ background: colors.gradients.heroSolid }}
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-10 sm:mb-12">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <Instagram
                            size={isMobile ? 28 : 32}
                            style={{ color: colors.accent.hover }}
                        />
                        <h2
                            className={`font-bold ${isMobile ? "text-2xl" : "text-3xl md:text-4xl"}`}
                            style={{ color: colors.text.primary }}
                        >
                            {t("instagram.title", "Senaste från Instagram")}
                        </h2>
                    </div>
                    <p
                        className={`max-w-2xl mx-auto ${isMobile ? "text-base" : "text-lg"}`}
                        style={{ color: colors.text.secondary }}
                    >
                        {t("instagram.subtitle", "Följ våra äventyr i Lappland")}
                    </p>
                </div>

                {/* Instagram Post Card */}
                <div className="max-w-3xl mx-auto">
                    {loading ? (
                        // Loading skeleton
                        <div
                            className="rounded-2xl overflow-hidden animate-pulse"
                            style={{ backgroundColor: colors.glass.dark }}
                        >
                            <div
                                className={`w-full ${isMobile ? "h-64" : "h-96"}`}
                                style={{ backgroundColor: colors.secondary }}
                            />
                            <div className="p-6 space-y-4">
                                <div className="h-4 rounded w-3/4" style={{ backgroundColor: colors.border }} />
                                <div className="h-4 rounded w-1/2" style={{ backgroundColor: colors.border }} />
                            </div>
                        </div>
                    ) : post ? (
                        <InteractiveCard
                            variant="tilt"
                            className="overflow-hidden rounded-2xl shadow-2xl"
                            style={{
                                backgroundColor: colors.secondary,
                                border: `1px solid ${colors.border}`,
                            }}
                        >
                            {/* Image/Video Container - Responsive: square on desktop, tall on mobile */}
                            <div
                                className={`relative overflow-hidden ${post.mediaType === "VIDEO"
                                        ? isMobile
                                            ? "aspect-[3/4]"  // Tall but not too tall for mobile
                                            : "aspect-square" // Square like Instagram feed on desktop
                                        : isMobile ? "aspect-[4/3]" : "aspect-video"
                                    }`}
                            >
                                {/* Show video player if playing, otherwise show thumbnail */}
                                {post.mediaType === "VIDEO" && isPlaying ? (
                                    <video
                                        ref={videoRef}
                                        src={post.videoUrl}
                                        className="w-full h-full object-cover"
                                        autoPlay
                                        loop
                                        playsInline
                                        controls
                                        onEnded={() => setIsPlaying(false)}
                                    />
                                ) : (
                                    <img
                                        src={post.imageUrl}
                                        alt={truncateCaption(post.caption, 100) || "Instagram post"}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                )}

                                {/* Play/Pause button for videos */}
                                {post.mediaType === "VIDEO" && (
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (isPlaying) {
                                                videoRef.current?.pause();
                                                setIsPlaying(false);
                                            } else {
                                                setIsPlaying(true);
                                            }
                                        }}
                                        className={`absolute inset-0 flex items-center justify-center cursor-pointer transition-opacity duration-300 ${isPlaying ? "opacity-0 hover:opacity-100" : "opacity-100"
                                            }`}
                                        style={{ background: isPlaying ? "rgba(0,0,0,0.3)" : "transparent" }}
                                        aria-label={isPlaying ? "Pause video" : "Play video"}
                                    >
                                        <div
                                            className="p-4 rounded-full backdrop-blur-sm transition-transform duration-300 hover:scale-110"
                                            style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
                                        >
                                            {isPlaying ? (
                                                <Pause size={32} fill="white" style={{ color: "white" }} />
                                            ) : (
                                                <Play size={32} fill="white" style={{ color: "white" }} />
                                            )}
                                        </div>
                                    </button>
                                )}

                                {/* Link to Instagram (only on images, or on video when not playing) */}
                                {post.mediaType !== "VIDEO" && (
                                    <a
                                        href={post.permalink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300 group"
                                        style={{ backgroundColor: "rgba(26, 47, 70, 0.7)" }}
                                    >
                                        <div className="flex items-center gap-2 text-white font-medium">
                                            <ExternalLink size={20} />
                                            <span>{t("instagram.viewOnInstagram", "Visa på Instagram")}</span>
                                        </div>
                                    </a>
                                )}
                            </div>


                            {/* Caption and metadata */}
                            <div className="p-5 sm:p-6">
                                {post.caption && (
                                    <p
                                        className={`mb-4 leading-relaxed ${isMobile ? "text-sm" : "text-base"}`}
                                        style={{ color: colors.text.primary }}
                                    >
                                        {truncateCaption(post.caption, isMobile ? 120 : 200)}
                                    </p>
                                )}

                                <div className="flex items-center justify-between">
                                    <span
                                        className="text-sm"
                                        style={{ color: colors.text.muted }}
                                    >
                                        {formatDate(post.timestamp)}
                                    </span>

                                    <a
                                        href="https://instagram.com/coldexperience.se"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105"
                                        style={{
                                            backgroundColor: colors.accent.primary,
                                            color: colors.text.primary,
                                        }}
                                    >
                                        <Instagram size={18} />
                                        <span className="text-sm font-medium">
                                            {t("instagram.follow", "Följ oss")}
                                        </span>
                                    </a>
                                </div>
                            </div>
                        </InteractiveCard>
                    ) : null}
                </div>
            </div>
        </section>
    );
};

export default InstagramFeed;
